import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

function StatementBuilderPage({ user }) {
  const navigate = useNavigate();
  
  const [form, setForm] = useState({
    incident_date: '',
    location: '',
    task_being_performed: '',
    user_role: '',
    what_happened: '',
    who_was_present: '',
    instructions_given: '',
    actions_taken: '',
    equipment_involved: '',
    what_happened_next: '',
    additional_details: '',
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.what_happened.length < 10) {
      setError('Please provide a more detailed description of what happened (at least 10 characters)');
      return;
    }

    setLoading(true);
    setError('');
    setResult(null);

    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/api/statement-builder/draft`,
        form,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      setResult(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate statement draft');
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setForm({
      incident_date: '',
      location: '',
      task_being_performed: '',
      user_role: '',
      what_happened: '',
      who_was_present: '',
      instructions_given: '',
      actions_taken: '',
      equipment_involved: '',
      what_happened_next: '',
      additional_details: '',
    });
    setResult(null);
    setError('');
  };

  const copyToClipboard = () => {
    if (result?.statement_draft) {
      navigator.clipboard.writeText(result.statement_draft);
    }
  };

  return (
    <div className="as-analytics-page" data-testid="statement-builder-page">
      <header className="as-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
          <img src="/stewie_front.png" alt="Stewie" data-testid="stewie-logo" style={{ width: 44, height: 44, borderRadius: '50%', objectFit: 'cover', objectPosition: 'center 25%', border: '2px solid #334155' }} />
          <div className="as-badge">Statement Tool</div>
        </div>
        <h1>Statement Builder</h1>
        <p>Organize your recollection into a clear, factual statement for workplace incidents.</p>
      </header>

      <div className="as-card">
        <div className="card-header">
          <h2>Build Your Statement</h2>
          <p>Answer the questions below to generate a structured statement draft</p>
        </div>
        <div className="card-body">
          {result ? (
            <div className="result-panel" data-testid="statement-result">
              <div className="result-header">
                <h3>Statement Draft</h3>
                <button className="btn-secondary" onClick={copyToClipboard}>
                  📋 Copy to Clipboard
                </button>
              </div>

              <div className="result-section">
                <h4>Your Draft Statement</h4>
                <pre className="fact-summary statement-draft">{result.statement_draft}</pre>
              </div>

              {result.missing_information.length > 0 && (
                <div className="result-section">
                  <h4>Missing Information</h4>
                  <p className="section-note">Consider adding these details to strengthen your statement:</p>
                  <ul className="missing-list">
                    {result.missing_information.map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}

              {result.clarifying_questions.length > 0 && (
                <div className="result-section">
                  <h4>Clarifying Questions</h4>
                  <p className="section-note">Think about these questions to add more detail:</p>
                  <ul className="checklist">
                    {result.clarifying_questions.map((q, idx) => (
                      <li key={idx}>{q}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="result-section">
                <h4>Tone Suggestions</h4>
                <ul className="checklist">
                  {result.tone_suggestions.map((tip, idx) => (
                    <li key={idx}>{tip}</li>
                  ))}
                </ul>
              </div>

              <div className="disclaimer-box">
                <strong>Disclaimer:</strong> {result.disclaimer}
              </div>

              <div className="button-row">
                <button className="btn-primary" onClick={handleClear}>Start Over</button>
                <button className="btn-secondary" onClick={() => window.print()}>Print Statement</button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="field-row">
                <div className="field-group half">
                  <label>Date of Incident</label>
                  <input
                    type="date"
                    value={form.incident_date}
                    onChange={(e) => updateField('incident_date', e.target.value)}
                    className="text-input"
                    data-testid="incident-date"
                  />
                </div>
                <div className="field-group half">
                  <label>Location</label>
                  <input
                    type="text"
                    value={form.location}
                    onChange={(e) => updateField('location', e.target.value)}
                    placeholder="Substation, yard, job site..."
                    className="text-input"
                    data-testid="location"
                  />
                </div>
              </div>

              <div className="field-row">
                <div className="field-group half">
                  <label>Task Being Performed</label>
                  <input
                    type="text"
                    value={form.task_being_performed}
                    onChange={(e) => updateField('task_being_performed', e.target.value)}
                    placeholder="Switching, inspection, maintenance..."
                    className="text-input"
                    data-testid="task-performed"
                  />
                </div>
                <div className="field-group half">
                  <label>Your Role</label>
                  <select
                    value={form.user_role}
                    onChange={(e) => updateField('user_role', e.target.value)}
                    className="select-input"
                    data-testid="user-role"
                  >
                    <option value="">Select...</option>
                    <option value="operator">Operator</option>
                    <option value="crew member">Crew Member</option>
                    <option value="crew leader">Crew Leader</option>
                    <option value="supervisor">Supervisor</option>
                    <option value="witness">Witness</option>
                  </select>
                </div>
              </div>

              <div className="field-group">
                <label>What Happened? *</label>
                <textarea
                  value={form.what_happened}
                  onChange={(e) => updateField('what_happened', e.target.value)}
                  placeholder="Describe the key event or incident in your own words. Focus on what you observed, heard, or did."
                  data-testid="what-happened"
                />
              </div>

              <div className="field-group">
                <label>Who Was Present?</label>
                <input
                  type="text"
                  value={form.who_was_present}
                  onChange={(e) => updateField('who_was_present', e.target.value)}
                  placeholder="List the people who were present during the event"
                  className="text-input"
                  data-testid="who-present"
                />
              </div>

              <div className="field-group">
                <label>Instructions Given</label>
                <textarea
                  value={form.instructions_given}
                  onChange={(e) => updateField('instructions_given', e.target.value)}
                  placeholder="What instructions did you receive before or during the work?"
                  data-testid="instructions-given"
                />
              </div>

              <div className="field-group">
                <label>Actions You Took</label>
                <textarea
                  value={form.actions_taken}
                  onChange={(e) => updateField('actions_taken', e.target.value)}
                  placeholder="Describe the specific actions you performed"
                  data-testid="actions-taken"
                />
              </div>

              <div className="field-group">
                <label>Equipment Involved</label>
                <input
                  type="text"
                  value={form.equipment_involved}
                  onChange={(e) => updateField('equipment_involved', e.target.value)}
                  placeholder="List any equipment, tools, or devices involved"
                  className="text-input"
                  data-testid="equipment-involved"
                />
              </div>

              <div className="field-group">
                <label>What Happened Next?</label>
                <textarea
                  value={form.what_happened_next}
                  onChange={(e) => updateField('what_happened_next', e.target.value)}
                  placeholder="Describe what occurred immediately after the event"
                  data-testid="what-happened-next"
                />
              </div>

              <div className="field-group">
                <label>Additional Details</label>
                <textarea
                  value={form.additional_details}
                  onChange={(e) => updateField('additional_details', e.target.value)}
                  placeholder="Any other relevant information you want to include"
                  data-testid="additional-details"
                />
              </div>

              {error && <div className="error-message" data-testid="error">{error}</div>}

              <div className="button-row">
                <button 
                  type="submit"
                  className="btn-primary" 
                  disabled={loading}
                  data-testid="generate-btn"
                >
                  {loading ? 'Generating...' : 'Generate Statement'}
                </button>
                <button type="button" className="btn-secondary" onClick={handleClear}>Clear Form</button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default StatementBuilderPage;
